'use client';
import React, { useEffect, useRef, useState } from 'react';

import loadBackgroudImages from '../../common/loadBackgroudImages';
function Header() {
  const videoRef = useRef(null);
  const headerRef = useRef(null);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoFrames, setVideoFrames] = useState([]);
  const [loadingVisible, setLoadingVisible] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const frameCountRef = useRef(0);

  useEffect(() => {
    // Simple mobile check
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    setIsMobile(isMobileDevice);
    console.log('Is mobile:', isMobileDevice);

    if (!isMobileDevice) {
      loadBackgroudImages();
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      if (!video || !canvas) return;
      
      // Desktop frame extraction logic continues as before...
      // ... existing code ...
    } else {
      // On mobile, fade in content after 4 seconds
      setContentVisible(false);
      const timer = setTimeout(() => setContentVisible(true), 8000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Main effect for video setup
  useEffect(() => {
    console.log('Video setup effect running, isMobile:', isMobile);
    loadBackgroudImages();

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video) {
      console.log('Video not found');
      return;
    }

    if (isMobile) {
      console.log('Setting up mobile video');
      // For mobile, just handle the video load and content fade
      const handleVideoLoad = () => {
        console.log('Mobile video loaded');
        setVideoLoaded(true);
        setLoadingVisible(false);
        setTimeout(() => {
          console.log('Setting content visible');
          setContentVisible(true);
        }, 200);
      };

      // Set video attributes
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('autoplay', 'true');
      video.setAttribute('loop', 'true');
      video.setAttribute('muted', 'true');

      video.addEventListener('loadeddata', handleVideoLoad);
      video.addEventListener('error', (e) => {
        console.error('Video error:', e);
      });
      
      // Log video attributes
      console.log('Video attributes:', {
        src: video.src,
        autoplay: video.autoplay,
        loop: video.loop,
        muted: video.muted,
        playsInline: video.playsInline
      });

      return () => {
        video.removeEventListener('loadeddata', handleVideoLoad);
      };
    }
    
    // Desktop checks
    if (!canvas) {
      console.log('Canvas not found for desktop view');
      return;
    }
    
    // Set up canvas context
    const context = canvas.getContext('2d', { alpha: false });
    contextRef.current = context;
    
    // Handle video metadata loaded to get duration
    const handleVideoMetadata = () => {
      // Pre-extract frames when video metadata is loaded
      extractFrames();
    };

    // Extract frames from video for smooth scrubbing
    const extractFrames = () => {
      if (!video || !context) return;
      
      // Aim for about 60 frames for smooth scrolling
      const targetFrameCount = 60;
      const frameDuration = video.duration / targetFrameCount;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const frames = [];
      frameCountRef.current = targetFrameCount;
      
      // Extract frames at regular intervals
      const extractNextFrame = (frameIndex) => {
        if (frameIndex >= targetFrameCount) {
          setVideoFrames(frames);
          setVideoLoaded(true);
          
          // After all frames are loaded, show the last frame initially
          if (frames.length > 0 && contextRef.current) {
            const lastFrame = frames[frames.length - 1];
            contextRef.current.drawImage(lastFrame, 0, 0, canvas.width, canvas.height);
          }
          
          // First fade out the loading screen
          setLoadingVisible(false);
          
          // Then fade in the content after a short delay
          setTimeout(() => {
            setContentVisible(true);
          }, 200);
          
          return;
        }
        
        const time = frameIndex * frameDuration;
        video.currentTime = time;
        
        // When seeking completes for this time
        const onSeeked = () => {
          // Draw current video frame to canvas
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Store the frame as image data
          const frameImage = new Image();
          frameImage.src = canvas.toDataURL('image/jpeg', 0.85);
          frames[frameIndex] = frameImage;
          
          // Move to next frame
          video.removeEventListener('seeked', onSeeked);
          setTimeout(() => extractNextFrame(frameIndex + 1), 0);
        };
        
        video.addEventListener('seeked', onSeeked, { once: true });
      };
      
      // Start extraction with frame 0
      extractNextFrame(0);
    };
    
    // Handle scroll to display appropriate frame
    const handleScroll = () => {
      if (!videoLoaded || videoFrames.length === 0) return;
      
      // Calculate which frame to show based on scroll position
      const headerHeight = headerRef.current?.offsetHeight || 1;
      const scrollPosition = window.scrollY;
      
      // Adjust scroll percentage to finish at 80% of header height
      const adjustedMaxHeight = headerHeight * 0.8; // Animation completes at 80% of header height
      const scrollPercentage = Math.min(scrollPosition / adjustedMaxHeight, 1);
      
      // Reverse the frame index (1.0 - scrollPercentage) to play in reverse during scroll
      const reverseScrollPercentage = 1.0 - scrollPercentage;
      
      // Determine frame index based on reversed scroll percentage
      const frameIndex = Math.min(
        Math.floor(reverseScrollPercentage * (frameCountRef.current - 1)),
        frameCountRef.current - 1
      );
      
      if (frameIndex >= 0 && frameIndex < videoFrames.length) {
        const frame = videoFrames[frameIndex];
        if (frame && contextRef.current) {
          contextRef.current.drawImage(frame, 0, 0, canvas.width, canvas.height);
        }
      }
    };
    
    // Load video and set up events
    video.addEventListener('loadedmetadata', handleVideoMetadata);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      video.removeEventListener('loadedmetadata', handleVideoMetadata);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [videoLoaded, videoFrames]);

  // Add effect to log state changes
  useEffect(() => {
    console.log('State changed:', { 
      videoLoaded, 
      loadingVisible, 
      contentVisible,
      isMobile 
    });
  }, [videoLoaded, loadingVisible, contentVisible, isMobile]);

  return (
    <header
      ref={headerRef}
      className= {isMobile ? "valign" : "header-personal valign"}
      data-overlay-dark="2"
    >
      {isMobile ? (
        // Simple mobile view
        <div style={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          ...(isMobile ? { paddingTop: '35%' } : {})
        }}>
          <video 
            ref={videoRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: -1
            }}
            src="/assets/video/vid15.mp4"
            autoPlay
            muted
            playsInline
            webkit-playsinline="true"
          />
          {/* Gradient overlay ON TOP of video */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 80%, rgba(0,0,0,0.9) 100%)',
              zIndex: 0
            }}
          />
          <div className="container ontop" style={{
            position: 'relative',
            zIndex: 1,
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}>
            <div className="row justify-content-center text-center">
              <div className="col-lg-8">
                <div className="caption">
                  <h1 className="fw-700 mb-10">
                    But first, your <span style={{color: 'black'}}>STORY</span>
                  </h1>
                  <h3 style={{ position: 'relative', height: 'auto', marginBottom: '20px' }}>
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      position: 'relative',
                      paddingTop: '20px'
                    }}>
                      <span>
                      Story Drives <span style={{color: 'black'}}>Business.</span>
                      </span>
                    </div>
                  </h3>
                  <h6 style={{ 
                    color: 'white',
                    marginTop: '10px',
                    marginBottom: '30px',
                    fontSize: '1.1rem',
                    fontWeight: '400',
                    lineHeight: '1.5',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    Narrative OS™ is the brand identity engine for the next generation.
                    <br />
                    In just 5 minutes, your scattered ideas become a clear Brand Blueprint™ — positioning,
                    <br />
                    manifesto, audience, voice, and visuals.
                  </h6>
                  <div className="row justify-content-center">
                    <div className="col-lg-10">
                      <div className="d-flex align-items-center justify-content-center mt-60">
                        <a
                          href="/brands/new"
                          className="butn butn-md radius-30"
                          style={{
                            backgroundColor: 'white', 
                            color: 'black',
                            border: '1px solid white',
                            transition: 'background-color 0.3s, color 0.3s',
                            textDecoration: 'none',
                            padding: '12px 35px',
                            fontWeight: '600',
                            fontSize: '16px',
                            display: 'inline-block',
                            marginTop: '30px',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.color = 'black';
                          }}
                        >
                          <span style={{color: 'inherit'}}>Start Your Brand BluePrint</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Desktop view - existing implementation
        <>
          {/* Hidden video used for frame extraction */}
          <video 
            ref={videoRef}
            className="bg-vid" 
            src="/assets/video/vid15.mp4" 
            muted 
            preload="auto"
            style={{ display: 'none' }}
          />
          
          {/* Canvas to display video frames */}
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              top: 0,
              left: 0,
              zIndex: -1
            }}
          />
          
          {/* Gradient overlay */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 80%, rgba(0,0,0,0.9) 100%)',
              zIndex: 0,
              pointerEvents: 'none'
            }}
          />
          
          {/* Loading screen with fade-out effect */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: loadingVisible ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1,
              opacity: videoLoaded ? 0 : 1,
              transition: 'opacity 0.8s ease',
              pointerEvents: videoLoaded ? 'none' : 'auto'
            }}
          >
            <p></p>
          </div>
          
          {/* Main content with fade-in effect */}
          <div 
            className="container ontop"
            style={{
              opacity: contentVisible ? 1 : 0,
              transition: 'opacity 0.8s ease',
              visibility: contentVisible ? 'visible' : 'hidden',
              position: 'relative',
              zIndex: 2,
              willChange: 'opacity'
            }}
          >
            <div className="row justify-content-center text-center">
              <div className="col-lg-8">
                <div className="caption">
                  <h6 className="mb-15">
                    <span className="icon-img-30 mr-10">
                    </span>
                  </h6>
                  <h1 className="fw-700 mb-10">
                    But first, your <span style={{color: 'black'}}>STORY</span>
                  </h1>
                  <h3 style={{ position: 'relative', height: 'auto', marginBottom: '20px' }}>
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      position: 'relative',
                      paddingTop: '20px'
                    }}>
                      <span style={{ 
                      }}>
                        Story Drives <span style={{color: 'black'}}>Business.</span>
                      </span>
                    </div>
                  </h3>
                  <h6 style={{ 
                    color: 'white',
                    marginTop: '10px',
                    marginBottom: '30px',
                    fontSize: '1.1rem',
                    fontWeight: '400',
                    lineHeight: '1.5',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    Narrative OS™ is the brand identity engine for the next generation.
                    <br />
                    In just 5 minutes, your scattered ideas become a clear Brand Blueprint™ — positioning,
                    <br />
                    manifesto, audience, voice, and visuals.
                  </h6>
                  <div className="row justify-content-center">
                    <div className="col-lg-10">
                      <div className="d-flex align-items-center justify-content-center mt-60">
                        <a
                          href="/brands/new"
                          className="butn butn-md radius-30"
                          style={{
                            backgroundColor: 'white', 
                            color: 'black',
                            border: '1px solid white',
                            transition: 'background-color 0.3s, color 0.3s',
                            textDecoration: 'none',
                            padding: '12px 35px',
                            fontWeight: '600',
                            fontSize: '16px',
                            display: 'inline-block',
                            marginTop: '30px',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.color = 'black';
                          }}
                        >
                          <span style={{color: 'inherit'}}>Start Your Brand BluePrint</span>
                        </a>
                      </div>

                      <div className="text mt-30">
                        <p style={{color: 'white'}}>
                          <span>&nbsp;</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="info d-flex align-items-center justify-content-center mt-100 flex-wrap">
              <div >
              </div>
              <div >
              </div><div >
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}

export default Header;
